import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Box,
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { Fragment, useState, type ReactNode } from 'react';
import type { PageInfo } from '../../../components/DataTable';

export interface ReportColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Content of the totals row under this column. */
  footer?: ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  hideBelow?: 'sm' | 'md' | 'lg';
}

/** A row under an expanded row, its cells keyed by column (lined up with the columns above). */
export type SubRow = (key: string, cells: Record<string, ReactNode>) => ReactNode;

/**
 * A report table: like the shared DataTable, plus a bold totals row, optional group header rows, and
 * borders that print well. Pages are 1-based.
 */
export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  totalsLabel = 'Total',
  showTotals = true,
  page,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  empty,
  toolbar,
  dense,
  bare,
  expand,
}: {
  columns: ReportColumn<T>[];
  rows: T[] | undefined;
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  totalsLabel?: string;
  showTotals?: boolean;
  page?: PageInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  toolbar?: ReactNode;
  dense?: boolean;
  /** No card around the table (when it sits inside another card). */
  bare?: boolean;
  /**
   * Rows that open: clicking one shows `expand(row, SubRow)` under it (e.g. the documents behind a
   * customer's total), as sub-rows lined up with the columns.
   */
  expand?: (row: T, subRow: SubRow) => ReactNode;
}) {
  const display = (c: ReportColumn<T>) =>
    c.hideBelow ? { display: { xs: 'none', [c.hideBelow]: 'table-cell' } } : {};
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const subRow: SubRow = (key, cells) => (
    <TableRow
      key={key}
      sx={{
        bgcolor: 'action.hover',
        '& td': { py: 1, fontSize: '0.8125rem', borderColor: 'divider' },
      }}
    >
      {columns.map((c, i) => (
        <TableCell key={c.key} align={c.align} sx={{ ...display(c), ...(i === 0 && { pl: 7 }) }}>
          {cells[c.key]}
        </TableCell>
      ))}
    </TableRow>
  );
  const hasFooter = showTotals && columns.some((c) => c.footer !== undefined);
  const table = (
    <>
      {toolbar && (
        <Box
          className="no-print"
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {toolbar}
        </Box>
      )}
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} sx={{ width: c.width, ...display(c) }}>
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={`s${String(i)}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} sx={display(c)}>
                      <Skeleton
                        width={c.align === 'right' ? '55%' : '75%'}
                        sx={{ ml: c.align === 'right' ? 'auto' : 0 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading &&
              rows?.map((row, i) => {
                const key = rowKey(row, i);
                const isOpen = open.has(key);
                const click = expand ? () => toggle(key) : onRowClick && (() => onRowClick(row));
                return (
                  <Fragment key={key}>
                    <TableRow
                      hover
                      onClick={click}
                      onKeyDown={
                        expand
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggle(key);
                              }
                            }
                          : undefined
                      }
                      tabIndex={expand ? 0 : undefined}
                      aria-expanded={expand ? isOpen : undefined}
                      sx={{
                        cursor: click ? 'pointer' : 'default',
                        ...(isOpen && { bgcolor: 'action.selected' }),
                        '&:focus-visible': {
                          outline: 2,
                          outlineColor: 'primary.main',
                          outlineOffset: -2,
                        },
                      }}
                    >
                      {columns.map((c, ci) => (
                        <TableCell key={c.key} align={c.align} sx={display(c)}>
                          {expand && ci === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                className="no-print"
                                sx={{
                                  width: 26,
                                  height: 26,
                                  flexShrink: 0,
                                  borderRadius: '8px',
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: isOpen ? 'primary.contrastText' : 'primary.main',
                                  bgcolor: isOpen ? 'primary.main' : 'action.selected',
                                  transition: 'background-color 150ms, color 150ms',
                                }}
                              >
                                <KeyboardArrowRightRounded
                                  sx={{
                                    fontSize: 20,
                                    transform: isOpen ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 150ms',
                                  }}
                                />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>{c.render(row)}</Box>
                            </Box>
                          ) : (
                            c.render(row)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expand && isOpen && expand(row, subRow)}
                  </Fragment>
                );
              })}
          </TableBody>
          {hasFooter && !loading && (rows?.length ?? 0) > 0 && (
            <TableFooter>
              <TableRow>
                {columns.map((c, i) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{
                      ...display(c),
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      borderTop: 2,
                      borderBottom: 0,
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {i === 0 && c.footer === undefined ? totalsLabel : c.footer}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      {!loading && rows?.length === 0 && <Box>{empty}</Box>}
      {page && onPageChange && page.total > 25 && (
        <TablePagination
          className="no-print"
          component="div"
          count={page.total}
          page={page.page - 1}
          rowsPerPage={page.pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => onPageChange(p + 1)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      )}
    </>
  );
  return bare ? table : <Card sx={{ minWidth: 0 }}>{table}</Card>;
}
